// Progress service: local-first, cloud-synced.
//
// Reliability model:
//  1. State lives in memory and is mirrored to localStorage on every change
//     (instant, works offline, survives reloads).
//  2. If Firebase is configured, we also subscribe to the user's Firestore doc.
//     On the first cloud snapshot we MERGE cloud + local (taking the best of
//     each per level) so progress is never lost when switching devices.
//  3. Local changes are written to Firestore (debounced, merge write). Firestore's
//     own offline cache queues writes made while offline.

import { firebaseReady } from "./firebase";

export interface LevelRecord {
  completed: boolean;
  stars: number; // 0..3
  bestScore?: number;
  bestMoves?: number;
  /** When this record was last improved. Records older than the reset epoch are dropped on merge. */
  ts?: number;
}

export interface GameProgress {
  highestUnlocked: number; // highest level the player may enter (1-based)
  levels: Record<string, LevelRecord>;
  /** When highestUnlocked was last raised (compared against the reset epoch on merge). */
  unlockTs?: number;
}

export interface Settings {
  sound: boolean;
  haptics: boolean;
}

/** The three cosmetic slots a player can equip one item into. */
export type CosmeticSlot = "palette" | "background" | "mascot";

export interface CosmeticsState {
  /** itemId -> purchase timestamp (compared against resetAt-epoch on merge). */
  owned: Record<string, number>;
  equipped: {
    palette: string; // palette id, "default" when nothing is bought
    background: string; // background id, "default"
    mascot: string; // mascot id, "default" = no mascot
  };
}

export interface Progress {
  coins: number;
  settings: Settings;
  games: Record<string, GameProgress>;
  cosmetics: CosmeticsState;
  updatedAt: number;
  /** When settings were last changed (for last-writer-wins on settings only). */
  settingsUpdatedAt: number;
  /** When game progress (levels/coins/unlocks) was last changed - NOT bumped by settings. */
  progressUpdatedAt: number;
  /** LWW timestamp for equipped cosmetics (same pattern as settingsUpdatedAt). */
  cosmeticsUpdatedAt: number;
  /** Daily streak: consecutive days a level was completed, as of `streakDay`. */
  streak: number;
  /** Local day index (days since epoch, local tz) of the last counted activity. */
  streakDay: number;
  /** Highest streak ever reached (cleared on reset). */
  bestStreak: number;
  /** LWW timestamp for the streak fields. */
  streakUpdatedAt: number;
  /**
   * "Reset epoch": a reset bumps this to now. Merging drops every level record
   * and unlock whose own timestamp predates the newest resetAt, so a reset can't
   * be undone by a stale device - not even one that wrote after the reset.
   */
  resetAt: number;
}

const LS_KEY = "logika:v1:progress";

function defaultCosmetics(): CosmeticsState {
  return { owned: {}, equipped: { palette: "default", background: "default", mascot: "default" } };
}

function defaultProgress(): Progress {
  return {
    coins: 0,
    settings: { sound: true, haptics: true },
    games: {},
    cosmetics: defaultCosmetics(),
    updatedAt: 0,
    settingsUpdatedAt: 0,
    progressUpdatedAt: 0,
    cosmeticsUpdatedAt: 0,
    streak: 0,
    streakDay: 0,
    bestStreak: 0,
    streakUpdatedAt: 0,
    resetAt: 0,
  };
}

/** Local-time day index (whole days since the Unix epoch in the user's timezone). */
function localDayIndex(now = Date.now()): number {
  const offsetMs = new Date().getTimezoneOffset() * 60000;
  return Math.floor((now - offsetMs) / 86400000);
}

function defaultGameProgress(): GameProgress {
  return { highestUnlocked: 1, levels: {} };
}

let state: Progress = loadLocal();
const listeners = new Set<(p: Progress) => void>();
let cloudWrite: ((p: Progress) => void) | null = null;
let writeTimer: ReturnType<typeof setTimeout> | null = null;

function loadLocal(): Progress {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return defaultProgress();
    const parsed = JSON.parse(raw) as Partial<Progress>;
    return {
      ...defaultProgress(),
      ...parsed,
      settings: { ...defaultProgress().settings, ...parsed.settings },
      cosmetics: {
        ...defaultCosmetics(),
        ...parsed.cosmetics,
        owned: { ...parsed.cosmetics?.owned },
        equipped: { ...defaultCosmetics().equipped, ...parsed.cosmetics?.equipped },
      },
    };
  } catch {
    return defaultProgress();
  }
}

function saveLocal(): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* storage full / private mode - in-memory state still works */
  }
}

function emit(): void {
  for (const fn of listeners) fn(state);
}

/** Apply a mutation, persist locally + (debounced) to cloud, and notify UI. */
function update(mutator: (p: Progress) => void): void {
  mutator(state);
  state.updatedAt = Date.now();
  saveLocal();
  emit();
  scheduleCloudWrite();
}

function scheduleCloudWrite(): void {
  if (!cloudWrite) return;
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => cloudWrite?.(state), 600);
}

// ---- Merge logic -----------------------------------------------------------

function mergeLevelRecord(a: LevelRecord | undefined, b: LevelRecord | undefined): LevelRecord {
  const x = a ?? { completed: false, stars: 0 };
  const y = b ?? { completed: false, stars: 0 };
  const merged: LevelRecord = {
    completed: x.completed || y.completed,
    stars: Math.max(x.stars, y.stars),
  };
  const bestScore = Math.max(x.bestScore ?? 0, y.bestScore ?? 0);
  if (bestScore > 0) merged.bestScore = bestScore;
  // For moves, lower is better; only consider records that have a value.
  const moves = [x.bestMoves, y.bestMoves].filter((m): m is number => typeof m === "number");
  if (moves.length) merged.bestMoves = Math.min(...moves);
  return merged;
}

/** Drop level records and unlocks that predate the reset epoch, then merge best-of. */
function mergeGameProgress(
  a: GameProgress | undefined,
  b: GameProgress | undefined,
  epoch: number,
): GameProgress | null {
  const x = a ?? defaultGameProgress();
  const y = b ?? defaultGameProgress();
  const levels: Record<string, LevelRecord> = {};
  for (const key of new Set([...Object.keys(x.levels), ...Object.keys(y.levels)])) {
    // A record survives only if its own timestamp is at or after the newest reset.
    // (Missing ts = legacy/pre-reset data: kept when there was never a reset.)
    const xr = x.levels[key] && (x.levels[key].ts ?? 0) >= epoch ? x.levels[key] : undefined;
    const yr = y.levels[key] && (y.levels[key].ts ?? 0) >= epoch ? y.levels[key] : undefined;
    if (xr || yr) levels[key] = mergeLevelRecord(xr, yr);
  }
  const xu = (x.unlockTs ?? 0) >= epoch ? x.highestUnlocked : 1;
  const yu = (y.unlockTs ?? 0) >= epoch ? y.highestUnlocked : 1;
  const merged: GameProgress = {
    highestUnlocked: Math.max(1, xu, yu),
    levels,
    unlockTs: Math.max(x.unlockTs ?? 0, y.unlockTs ?? 0),
  };
  // A game entry with no surviving data doesn't need to exist at all.
  if (merged.highestUnlocked === 1 && Object.keys(levels).length === 0) return null;
  return merged;
}

/** Merge two full progress objects, taking the best of each, honoring resets. */
export function mergeProgress(a: Progress, b: Progress): Progress {
  const epoch = Math.max(a.resetAt, b.resetAt);

  // Per-level/per-unlock timestamps decide what survives a reset, so a stale
  // device that writes AFTER the reset can only contribute what it genuinely
  // achieved after the reset - its old records carry old timestamps and drop out.
  const games: Record<string, GameProgress> = {};
  for (const key of new Set([...Object.keys(a.games), ...Object.keys(b.games)])) {
    const merged = mergeGameProgress(a.games[key], b.games[key], epoch);
    if (merged) games[key] = merged;
  }

  // Coins: last-writer-wins on game progress (a document-level counter can't be
  // epoch-filtered per item; LWW keeps resets authoritative).
  const aCoinsValid = a.progressUpdatedAt >= epoch;
  const bCoinsValid = b.progressUpdatedAt >= epoch;
  let coins = 0;
  if (aCoinsValid && bCoinsValid) coins = a.progressUpdatedAt >= b.progressUpdatedAt ? a.coins : b.coins;
  else if (aCoinsValid) coins = a.coins;
  else if (bCoinsValid) coins = b.coins;

  // Settings: last-writer-wins by their own timestamp (not the general updatedAt,
  // which bumps on every gameplay mutation).
  const settingsSource = a.settingsUpdatedAt >= b.settingsUpdatedAt ? a : b;

  // Cosmetics owned: union across devices, epoch-filtered like level records
  // (an item survives only if its own purchase ts is at/after the newest reset).
  // Keep the OLDEST valid timestamp so a later reset can still invalidate it.
  const aCos = a.cosmetics ?? defaultCosmetics();
  const bCos = b.cosmetics ?? defaultCosmetics();
  const owned: Record<string, number> = {};
  for (const key of new Set([...Object.keys(aCos.owned), ...Object.keys(bCos.owned)])) {
    const av = typeof aCos.owned[key] === "number" && aCos.owned[key] >= epoch ? aCos.owned[key] : undefined;
    const bv = typeof bCos.owned[key] === "number" && bCos.owned[key] >= epoch ? bCos.owned[key] : undefined;
    const valid = [av, bv].filter((x): x is number => typeof x === "number");
    if (valid.length) owned[key] = Math.min(...valid);
  }

  // Equipped: whole object from the writer with the newest cosmeticsUpdatedAt,
  // then guard - an equipped item that isn't owned (and isn't "default", e.g.
  // after a reset dropped it) falls back to "default".
  const cosmeticsSource = a.cosmeticsUpdatedAt >= b.cosmeticsUpdatedAt ? aCos : bCos;
  // Older payloads may predate a slot (e.g. mascot) - default the whole shape first.
  const equipped = { ...defaultCosmetics().equipped, ...cosmeticsSource.equipped };
  if (equipped.palette !== "default" && !(equipped.palette in owned)) equipped.palette = "default";
  if (equipped.background !== "default" && !(equipped.background in owned)) equipped.background = "default";
  if (equipped.mascot !== "default" && !(equipped.mascot in owned)) equipped.mascot = "default";

  // Daily streak: last-writer-wins by its own timestamp, epoch-filtered like
  // coins; bestStreak takes the max of the sides that survive the reset.
  const aStreakValid = (a.streakUpdatedAt ?? 0) >= epoch;
  const bStreakValid = (b.streakUpdatedAt ?? 0) >= epoch;
  const streakSrc =
    aStreakValid && bStreakValid ? (a.streakUpdatedAt >= b.streakUpdatedAt ? a : b) : aStreakValid ? a : bStreakValid ? b : null;
  const streak = streakSrc?.streak ?? 0;
  const streakDay = streakSrc?.streakDay ?? 0;
  let bestStreak = 0;
  if (aStreakValid) bestStreak = Math.max(bestStreak, a.bestStreak ?? 0);
  if (bStreakValid) bestStreak = Math.max(bestStreak, b.bestStreak ?? 0);

  return {
    coins,
    settings: { ...settingsSource.settings },
    games,
    cosmetics: { owned, equipped },
    updatedAt: Math.max(a.updatedAt, b.updatedAt),
    settingsUpdatedAt: Math.max(a.settingsUpdatedAt, b.settingsUpdatedAt),
    progressUpdatedAt: Math.max(a.progressUpdatedAt, b.progressUpdatedAt),
    cosmeticsUpdatedAt: Math.max(a.cosmeticsUpdatedAt, b.cosmeticsUpdatedAt),
    streak,
    streakDay,
    bestStreak,
    streakUpdatedAt: Math.max(a.streakUpdatedAt ?? 0, b.streakUpdatedAt ?? 0),
    resetAt: epoch,
  };
}

// ---- Public API ------------------------------------------------------------

export function getProgress(): Progress {
  return state;
}

export function subscribe(fn: (p: Progress) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

export function getGameProgress(gameId: string): GameProgress {
  return state.games[gameId] ?? defaultGameProgress();
}

export function getLevelRecord(gameId: string, level: number): LevelRecord | undefined {
  return state.games[gameId]?.levels[String(level)];
}

export function isLevelUnlocked(gameId: string, level: number): boolean {
  return level <= getGameProgress(gameId).highestUnlocked;
}

export function getSettings(): Settings {
  return state.settings;
}

export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
  update((p) => {
    p.settings[key] = value;
    p.settingsUpdatedAt = Date.now();
  });
}

export function addCoins(amount: number): void {
  update((p) => {
    p.coins = Math.max(0, p.coins + amount);
    p.progressUpdatedAt = Date.now();
  });
}

/**
 * Record a finished level. Keeps the best stars/score, lowest move count,
 * and unlocks the next level. Coins reward is granted only the first time a
 * level is completed.
 */
export function recordLevelResult(
  gameId: string,
  level: number,
  result: { stars: number; score?: number; moves?: number },
  totalLevels: number,
): { firstClear: boolean; coinsEarned: number; streak: number; streakBumped: boolean } {
  let firstClear = false;
  let coinsEarned = 0;
  let streak = 0;
  let streakBumped = false;
  update((p) => {
    const now = Date.now();
    const game = (p.games[gameId] ??= defaultGameProgress());
    const key = String(level);
    const prev = game.levels[key];
    firstClear = !prev?.completed;

    const next: LevelRecord = mergeLevelRecord(prev, {
      completed: true,
      stars: result.stars,
      bestScore: result.score,
      bestMoves: result.moves,
    });
    next.ts = now;
    game.levels[key] = next;

    if (level < totalLevels && level + 1 > game.highestUnlocked) {
      // Stamp only on a real increase, so a stale pre-reset unlock value can't
      // ride along on a fresh timestamp.
      game.highestUnlocked = level + 1;
      game.unlockTs = now;
    }
    if (firstClear) {
      coinsEarned = 10 + result.stars * 5;
      p.coins += coinsEarned;
    }
    // Daily streak: count today once. A same-day replay doesn't re-bump; a gap
    // of more than one day restarts the streak at 1.
    const day = localDayIndex(now);
    if (p.streakDay !== day) {
      p.streak = p.streakDay === day - 1 ? p.streak + 1 : 1;
      p.streakDay = day;
      p.bestStreak = Math.max(p.bestStreak, p.streak);
      p.streakUpdatedAt = now;
      streakBumped = true;
    }
    streak = p.streak;
    p.progressUpdatedAt = now;
  });
  return { firstClear, coinsEarned, streak, streakBumped };
}

/** Unlock a level without completing it (used by the skip button to test ahead). */
export function unlockLevel(gameId: string, level: number): void {
  update((p) => {
    const game = (p.games[gameId] ??= defaultGameProgress());
    if (level > game.highestUnlocked) {
      game.highestUnlocked = level;
      game.unlockTs = Date.now();
    }
    p.progressUpdatedAt = Date.now();
  });
}

export function resetProgress(): void {
  update((p) => {
    p.coins = 0;
    p.games = {};
    p.cosmetics = defaultCosmetics();
    p.streak = 0;
    p.streakDay = 0;
    p.bestStreak = 0;
    p.streakUpdatedAt = Date.now();
    p.resetAt = Date.now(); // tombstone: invalidates any older progress on merge
    p.progressUpdatedAt = Date.now();
    p.cosmeticsUpdatedAt = Date.now();
  });
}

/**
 * Current daily streak for display. `current` is the stored streak if it's still
 * alive (a level was completed today or yesterday); a missed day makes it 0 until
 * the next completion restarts it. `playedToday` tells the UI it's already safe.
 */
export function getStreak(): { current: number; best: number; playedToday: boolean } {
  const today = localDayIndex();
  const alive = state.streakDay === today || state.streakDay === today - 1;
  return { current: alive ? state.streak : 0, best: state.bestStreak, playedToday: state.streakDay === today };
}

// ---- Cosmetics API ---------------------------------------------------------

export function getCosmetics(): CosmeticsState {
  return state.cosmetics;
}

/** "default" always counts as owned; otherwise the item must be in `owned`. */
export function ownsCosmetic(id: string): boolean {
  return id === "default" || id in state.cosmetics.owned;
}

/**
 * Atomically buy and equip an item. Returns false (state untouched) if the
 * player can't afford it or already owns it.
 */
export function buyCosmetic(id: string, price: number, type: CosmeticSlot): boolean {
  if (ownsCosmetic(id) || state.coins < price) return false;
  update((p) => {
    const now = Date.now();
    p.coins -= price;
    p.cosmetics.owned[id] = now;
    p.cosmetics.equipped[type] = id;
    p.progressUpdatedAt = now;
    p.cosmeticsUpdatedAt = now;
  });
  return true;
}

/** Equip an already-owned item (no-op if not owned). */
export function equipCosmetic(type: CosmeticSlot, id: string): void {
  if (!ownsCosmetic(id)) return;
  update((p) => {
    p.cosmetics.equipped[type] = id;
    p.cosmeticsUpdatedAt = Date.now();
  });
}

// ---- Cloud sync ------------------------------------------------------------

let cloudStatus: "local" | "syncing" | "synced" = "local";
const statusListeners = new Set<(s: typeof cloudStatus) => void>();

// Fired ONLY when a change arrives from another device (not on local writes),
// so the UI can refresh without disrupting an in-progress level.
const remoteListeners = new Set<() => void>();
export function onRemoteUpdate(fn: () => void): () => void {
  remoteListeners.add(fn);
  return () => remoteListeners.delete(fn);
}

export function getCloudStatus(): typeof cloudStatus {
  return cloudStatus;
}
export function onCloudStatus(fn: (s: typeof cloudStatus) => void): () => void {
  statusListeners.add(fn);
  fn(cloudStatus);
  return () => statusListeners.delete(fn);
}
function setCloudStatus(s: typeof cloudStatus): void {
  cloudStatus = s;
  for (const fn of statusListeners) fn(s);
}

/**
 * Connect to Firestore (if configured). Safe to call once at startup; if no
 * config is present it quietly does nothing and the app stays local-only.
 */
export async function initCloudSync(): Promise<void> {
  const handles = await firebaseReady();
  if (!handles) return;
  setCloudStatus("syncing");

  const { db, uid } = handles;
  const { doc, onSnapshot, setDoc } = await import("firebase/firestore");
  const ref = doc(db, "users", uid);

  let firstSnapshot = true;
  cloudWrite = (p) => {
    // Full-document overwrite (no merge): we always send the complete, already-
    // merged local state, so this keeps the cloud doc exactly in sync AND lets a
    // reset actually remove cleared keys (merge:true would leave them behind).
    setDoc(ref, { ...p }).catch((err) =>
      console.warn("[Logika] cloud write failed (queued offline):", err),
    );
  };

  onSnapshot(
    ref,
    (snap) => {
      const cloud = snap.data() as Progress | undefined;
      if (firstSnapshot) {
        firstSnapshot = false;
        if (cloud) {
          state = mergeProgress(state, { ...defaultProgress(), ...cloud });
          saveLocal();
          emit();
        }
        // Push merged state up so the cloud has our local-only progress too.
        cloudWrite?.(state);
      } else if (cloud && !snap.metadata.hasPendingWrites) {
        // Remote change from another device: merge it in.
        const merged = mergeProgress(state, { ...defaultProgress(), ...cloud });
        state = merged;
        saveLocal();
        emit();
        for (const fn of remoteListeners) fn();
      }
      setCloudStatus("synced");
    },
    (err) => {
      console.warn("[Logika] snapshot error:", err);
      setCloudStatus("local");
    },
  );
}
